import { useState } from 'react';
import {
  ArrowLeft,
  FileText,
  Shield,
  Database,
  BookOpen,
  Tags,
  Link2,
  GitBranch,
  MessageSquare,
  Network,
  Inbox,
  ChevronRight,
} from 'lucide-react';
import DocumentManagement from './DocumentManagement';
import IngestionHealth from './IngestionHealth';
import FrontmatterEditor from './FrontmatterEditor';
import DomainTermsEditor from './DomainTermsEditor';
import SynonymMappingsEditor from './SynonymMappingsEditor';
import ImageToFlow from './ImageToFlow';
import TrainedQaEditor from './TrainedQaEditor';
import QaReviewQueue from './QaReviewQueue';
import ConceptGraphEditor from './ConceptGraphEditor';
import ModuleGroupingEditor from './ModuleGroupingEditor';
import EntityTypeEditor from './EntityTypeEditor';
import MasterLookupsEditor from './MasterLookupsEditor';
import EvidenceChainsEditor from './EvidenceChainsEditor';
import ModuleLinkStoplistEditor from './ModuleLinkStoplistEditor';

type Tab = 'documents' | 'health' | 'frontmatter' | 'domain-terms' | 'synonym-mappings' | 'image-to-flow' | 'qa-management' | 'qa-review-queue' | 'concept-graph' | 'module-groupings' | 'entity-types' | 'master-lookups' | 'evidence-chains' | 'module-stoplist';

interface TabItem {
  id: Tab;
  label: string;
  icon: React.ReactNode;
}

interface TabGroup {
  id: string;
  name: string;
  icon: React.ReactNode;
  tabs: TabItem[];
}

interface DocProcessingPageProps {
  onBack: () => void;
}

export default function DocProcessingPage({ onBack }: DocProcessingPageProps) {
  const [activeTab, setActiveTab] = useState<Tab>('documents');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);

  const tabGroups: TabGroup[] = [
    {
      id: 'document-mgmt',
      name: 'Document Management',
      icon: <FileText size={20} />,
      tabs: [
        { id: 'documents', label: 'Document Management', icon: <FileText size={16} /> },
        { id: 'frontmatter', label: 'Frontmatter', icon: <BookOpen size={16} /> },
        { id: 'domain-terms', label: 'Domain Terms', icon: <Tags size={16} /> },
        { id: 'synonym-mappings', label: 'Synonym', icon: <Link2 size={16} /> },
        { id: 'image-to-flow', label: 'Image to Flow', icon: <GitBranch size={16} /> },
        { id: 'concept-graph', label: 'Knowledge Graph', icon: <Network size={16} /> },
        { id: 'health', label: 'Ingestion Health', icon: <Shield size={16} /> },
      ],
    },
    {
      id: 'metadata-config',
      name: 'Metadata Configuration',
      icon: <Database size={20} />,
      tabs: [
        { id: 'module-groupings', label: 'Module Groupings', icon: <Tags size={16} /> },
        { id: 'entity-types', label: 'Entity Types', icon: <Tags size={16} /> },
        { id: 'master-lookups', label: 'Master Lookups', icon: <Database size={16} /> },
        { id: 'evidence-chains', label: 'Evidence Chains', icon: <Link2 size={16} /> },
        { id: 'module-stoplist', label: 'Module Stoplist', icon: <Tags size={16} /> },
      ],
    },
    {
      id: 'quality-review',
      name: 'Quality & Review',
      icon: <MessageSquare size={20} />,
      tabs: [
        { id: 'qa-management', label: 'Q&A Management', icon: <MessageSquare size={16} /> },
        { id: 'qa-review-queue', label: 'Review Queue', icon: <Inbox size={16} /> },
      ],
    },
  ];

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Doc Processing</h1>
          <p className="text-sm text-gray-500">Document management, ingestion, and system health</p>
        </div>
      </div>

      {/* Two-column layout: Sidebar + Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Collapsible */}
        <div
          className={`bg-white border-r border-gray-200 transition-all duration-300 ease-in-out overflow-hidden flex flex-col ${
            sidebarCollapsed ? 'w-20' : 'w-60'
          }`}
          onMouseEnter={() => setHoveredGroup(null)}
        >
          {/* Sidebar Toggle Button */}
          <div className="p-2 border-b border-gray-200">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="w-full flex items-center justify-center p-2 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronRight
                size={20}
                className={`transition-transform ${sidebarCollapsed ? '' : 'rotate-180'}`}
              />
            </button>
          </div>

          {/* Navigation Groups */}
          <div className="flex-1 overflow-y-auto">
            {tabGroups.map((group) => (
              <div key={group.id}>
                {/* Group Header - Always visible */}
                <div
                  className="relative group"
                  onMouseEnter={() => !sidebarCollapsed && setHoveredGroup(null)}
                >
                  <button
                    onClick={() => !sidebarCollapsed && setHoveredGroup(hoveredGroup === group.id ? null : group.id)}
                    onMouseEnter={() => sidebarCollapsed && setHoveredGroup(group.id)}
                    title={group.name}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 transition-colors border-l-4 ${
                      tabGroups.find(g => g.tabs.some(t => t.id === activeTab))?.id === group.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-transparent'
                    }`}
                  >
                    <div className="flex-shrink-0">{group.icon}</div>
                    {!sidebarCollapsed && (
                      <span className="text-sm font-medium truncate">{group.name}</span>
                    )}
                  </button>

                  {/* Expanded menu - appears on hover when collapsed */}
                  {sidebarCollapsed && hoveredGroup === group.id && (
                    <div className="absolute left-full top-0 ml-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-56">
                      <div className="p-2">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                          {group.name}
                        </div>
                        {group.tabs.map((tab) => (
                          <button
                            key={tab.id}
                            onClick={() => {
                              setActiveTab(tab.id);
                              setHoveredGroup(null);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors text-left ${
                              activeTab === tab.id
                                ? 'bg-blue-100 text-blue-700 font-medium'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {tab.icon}
                            {tab.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Expanded tabs - shown when sidebar is not collapsed */}
                {!sidebarCollapsed && (
                  <div className="bg-gray-50">
                    {group.tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-left ml-4 ${
                          activeTab === tab.id
                            ? 'bg-blue-100 text-blue-700 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {tab.icon}
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-6">
            {activeTab === 'documents' && <DocumentManagement />}
            {activeTab === 'frontmatter' && <FrontmatterEditor />}
            {activeTab === 'domain-terms' && <DomainTermsEditor />}
            {activeTab === 'synonym-mappings' && <SynonymMappingsEditor />}
            {activeTab === 'module-groupings' && <ModuleGroupingEditor />}
            {activeTab === 'entity-types' && <EntityTypeEditor />}
            {activeTab === 'master-lookups' && <MasterLookupsEditor />}
            {activeTab === 'evidence-chains' && <EvidenceChainsEditor />}
            {activeTab === 'module-stoplist' && <ModuleLinkStoplistEditor />}
            {activeTab === 'image-to-flow' && <ImageToFlow />}
            {activeTab === 'qa-management' && <TrainedQaEditor />}
            {activeTab === 'qa-review-queue' && <QaReviewQueue />}
            {activeTab === 'concept-graph' && <ConceptGraphEditor />}
            {activeTab === 'health' && <IngestionHealth />}
          </div>
        </div>
      </div>
    </div>
  );
}
